import { useId, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import {
  EMPTY_QUOTATION_DETAIL_FILTERS,
  countQuotationDetailFilters,
  parseQuotationDetailPrice,
  type QuotationDetailFilters,
} from "@/features/quotation/detailFilters";

type FilterOption = { value: string; label: string };

type QuotationDetailFilterPopoverProps = {
  mode: "manual" | "official";
  filters: QuotationDetailFilters;
  categories: FilterOption[];
  suppliers: FilterOption[];
  onApply: (filters: QuotationDetailFilters) => void;
  onClear: () => void;
};

export function QuotationDetailFilterPopover({
  mode,
  filters,
  categories,
  suppliers,
  onApply,
  onClear,
}: QuotationDetailFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);
  const [minimumPrice, setMinimumPrice] = useState("");
  const [maximumPrice, setMaximumPrice] = useState("");
  const errorId = useId();
  const parsedMinimumPrice = parseQuotationDetailPrice(minimumPrice);
  const parsedMaximumPrice = parseQuotationDetailPrice(maximumPrice);
  const priceError =
    parsedMinimumPrice === undefined || parsedMaximumPrice === undefined
      ? "Use números no formato brasileiro, como 12,50."
      : parsedMinimumPrice !== null &&
          parsedMaximumPrice !== null &&
          parsedMinimumPrice > parsedMaximumPrice
        ? "O preço mínimo não pode ser maior que o preço máximo."
        : null;

  return (
    <div className="lnk-filter-control quotation-detail-filter">
      <OperationalFilterSurface
        open={open}
        title="Filtrar"
        activeCount={countQuotationDetailFilters(filters)}
        applyDisabled={priceError !== null}
        onOpenChange={(next) => {
          if (next) {
            setDraft(filters);
            setMinimumPrice(
              filters.minimumPrice?.toString().replace(".", ",") ?? "",
            );
            setMaximumPrice(
              filters.maximumPrice?.toString().replace(".", ",") ?? "",
            );
          }
          setOpen(next);
        }}
        onClear={() => {
          onClear();
          setDraft(EMPTY_QUOTATION_DETAIL_FILTERS);
          setMinimumPrice("");
          setMaximumPrice("");
          setOpen(false);
        }}
        onApply={() => {
          onApply({
            ...draft,
            minimumPrice: parsedMinimumPrice ?? null,
            maximumPrice: parsedMaximumPrice ?? null,
          });
        }}
      >
        <button
          type="button"
          className="quotation-detail-filter__regular"
          aria-pressed={draft.offer === "regular"}
          onClick={() =>
            setDraft((current) => ({
              ...current,
              offer: current.offer === "regular" ? "all" : "regular",
            }))
          }
        >
          Só regulares
        </button>
        <div className="lnk-filter-state-grid">
          {(
            [
              ["all", "Todos"],
              ["selected", "Na cotação"],
              ["offers", "Ofertas"],
              [
                "pending",
                mode === "official" ? "Para selecionar" : "Indisponíveis",
              ],
            ] as const
          ).map(([value, label]) => {
            const active =
              value === "all"
                ? draft.state === "all" && draft.offer === "all"
                : value === "offers"
                  ? draft.offer === "offers"
                  : draft.state === value;
            return (
              <button
                key={value}
                type="button"
                className={active ? "active" : ""}
                aria-pressed={active}
                onClick={() =>
                  setDraft((current) =>
                    value === "all"
                      ? { ...current, state: "all", offer: "all" }
                      : value === "offers"
                        ? {
                            ...current,
                            offer:
                              current.offer === "offers" ? "all" : "offers",
                          }
                        : {
                            ...current,
                            state: current.state === value ? "all" : value,
                          },
                  )
                }
              >
                <span>{label}</span>
                {active ? <CheckCircle2 size={15} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
        <OperationalSelect
          value={draft.category}
          menuLayer="dialog"
          options={[{ value: "all", label: "Todos os tipos" }, ...categories]}
          onChange={(category) =>
            setDraft((current) => ({
              ...current,
              category,
            }))
          }
          ariaLabel="Tipo"
        />
        <OperationalSelect
          value={draft.supplierId}
          menuLayer="dialog"
          options={[
            { value: "all", label: "Todos os fornecedores" },
            ...suppliers,
          ]}
          onChange={(supplierId) =>
            setDraft((current) => ({
              ...current,
              supplierId,
            }))
          }
          ariaLabel="Fornecedor"
        />
        <fieldset className="quotation-detail-filter__price">
          <legend>Faixa de preço</legend>
          <input
            aria-label="Preço mínimo"
            placeholder="Mínimo"
            inputMode="decimal"
            aria-invalid={parsedMinimumPrice === undefined}
            aria-describedby={priceError ? errorId : undefined}
            value={minimumPrice}
            onChange={(event) => setMinimumPrice(event.target.value)}
          />
          <span aria-hidden="true">—</span>
          <input
            aria-label="Preço máximo"
            placeholder="Máximo"
            inputMode="decimal"
            aria-invalid={parsedMaximumPrice === undefined}
            aria-describedby={priceError ? errorId : undefined}
            value={maximumPrice}
            onChange={(event) => setMaximumPrice(event.target.value)}
          />
        </fieldset>
        {priceError ? (
          <p id={errorId} role="alert" className="quotation-dialog-error">
            {priceError}
          </p>
        ) : null}
      </OperationalFilterSurface>
    </div>
  );
}
